<template>
  <div ref="wrapper" :class="className" tabindex="-1" role="group">
    <div
      :class="{
        [ns.e('filler')]: true,
        [ns.em('filler', 'loading')]: isLoading,
        [ns.em('filler', 'success')]: isSuccess,
      }"
      :style="fillerStyle"
    />
    <div
      :class="{
        [ns.e('tip')]: true,
        [ns.em('tip', 'focused')]: dragging,
        [ns.em('tip', 'loading')]: isLoading,
        [ns.em('tip', 'success')]: isSuccess,
      }"
      :style="tipStyle"
    >
      <slot name="tip" :success="isSuccess">
        {{
          isSuccess
            ? props.successTip ?? textInfo.success
            : props.tip ?? textInfo.slideEnd
        }}
      </slot>
    </div>
    <div ref="track" :class="ns.e('track')">
      <div
        ref="trigger"
        :class="{
          [ns.e('trigger')]: true,
          [ns.em('trigger', 'focused')]: dragging,
          [ns.em('trigger', 'loading')]: isLoading,
          [ns.em('trigger', 'success')]: isSuccess,
        }"
        tabindex="0"
        :style="triggerStyle"
        @transitionend="afterReset"
      >
        <slot name="trigger" :success="isSuccess">
          <LtIcon :size="12">
            <Check v-if="isSuccess" />
            <Loading v-else-if="isLoading" />
            <DArrowRight v-else />
          </LtIcon>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { LtIcon } from '@lit-element/components/icon'
import { Check, DArrowRight, Loading } from '@inte-cloud/icons-vue'
import { boundRange, toFixed } from './hooks/number'
import { useMoving } from './hooks/moving'
import { useSetTimeout } from './hooks/useSetTimeout'
import { captchaSliderProps, textInfo } from './data'

defineOptions({ name: 'LtCaptchaSlider' })

const props = defineProps(captchaSliderProps)

const ns = useNamespace('captcha')

const { timer } = useSetTimeout()

const currentLeft = ref(0)
const testing = ref(false)
const resetting = ref(false)
const isSuccess = ref(false)
const testLoading = ref(false)

const track = ref<HTMLElement>()

const readonly = computed(() => {
  return props.disabled || (props.loading && props.loadingLock)
})

let widthLimit: number

const { target: trigger, moving: dragging } = useMoving({
  onStart: (_, event) => {
    resetting.value = false
    if (
      testing.value ||
      readonly.value ||
      !track.value ||
      !trigger.value ||
      isSuccess.value ||
      resetting.value ||
      event.button > 0
    ) {
      return false
    }

    widthLimit = track.value.getBoundingClientRect().width
    currentLeft.value = 0
    verifyPosition()
    trigger.value.focus()
    props.onDragStart?.(currentLeft.value)
  },
  onMove: (state) => {
    if (testing.value || readonly.value || isSuccess.value || resetting.value) {
      return false
    }

    currentLeft.value = (state.deltaX / widthLimit) * 100
    verifyPosition()
    props.onDrag?.(currentLeft.value)
  },
  onEnd: async () => {
    if (testing.value || readonly.value) return

    testing.value = true

    const matched = matchTarget(currentLeft.value)
    let customResult: unknown

    if (typeof props.onBeforeTest === 'function') {
      nextTick(() => {
        testLoading.value = true
      })
      customResult = await props.onBeforeTest(currentLeft.value, matched)
      nextTick(() => {
        testLoading.value = false
      })
    }

    if (
      currentLeft.value &&
      (customResult === false || (!matched && customResult !== true))
    ) {
      resetting.value = true
      currentLeft.value = 0
      isSuccess.value = false
      props.onFail?.()
    } else if (matched || customResult === true) {
      isSuccess.value = true

      if (customResult && !matched) {
        resetting.value = true
        currentLeft.value = props.target
      }
      props.onSuccess?.(currentLeft.value)
    }

    trigger.value?.blur()
    props.onDragEnd?.(currentLeft.value)
    clearTimeout(timer.testing)
    testing.value = false
  },
})

const isLoading = computed(() => props.loading || testLoading.value)
const className = computed(() => {
  const baseCls = ns.e('slider')

  return {
    [baseCls]: true,
    [ns.b('vars')]: true,
    [`${baseCls}--success`]: isSuccess.value,
    [`${baseCls}--disabled`]: props.disabled,
    [`${baseCls}--loading`]: isLoading.value,
    [`${baseCls}--${props.size}`]: props.size !== 'default',
  }
})
const fillerStyle = computed(() => {
  return {
    [`--${ns.b('filler-transition')}`]: resetting.value
      ? 'transform 250ms ease'
      : undefined,
    transform: `scaleX(${currentLeft.value / 100})`,
  }
})
const tipStyle = computed(() => {
  return {
    transition: resetting.value ? 'background-position 250ms ease' : undefined,
    backgroundPosition: `-${currentLeft.value}%`,
  }
})
const triggerStyle = computed(() => {
  return {
    left: `${currentLeft.value}%`,
    transition: resetting.value ? 'left 250ms ease' : undefined,
  }
})

watch(readonly, (value) => {
  if (value) {
    reset()
  }
})

defineExpose({
  currentLeft,
  resetting,
  isSuccess,
  dragging,
  isLoading,
  track,
  trigger,
  focus,
  reset,
})

function verifyPosition() {
  currentLeft.value = toFixed(boundRange(currentLeft.value, 0, 100), 3)
}

function reset() {
  resetting.value = true
  currentLeft.value = 0
  isSuccess.value = false
}

function afterReset() {
  resetting.value = false
}

function matchTarget(value: number) {
  return Math.abs(props.target - value) <= props.tolerance
}

function focus(options?: FocusOptions) {
  trigger.value?.focus(options)
}
</script>
